import {
  V1_SCHEMA_VERSION,
  validateMatchmakingInputV1,
  runMatchmakingFromFrozenInput,
} from '@badminton/matchmaking-core';
import { MATCHMAKING_CONFIG } from '@badminton/matchmaking-core/pure/matchmakingConfig.js';

function playerName(player) {
  if (!player) return '';
  if (player.name) return player.name;
  return `${player.first_name || ''} ${player.last_name || ''}`.trim();
}

function asPlayerId(value) {
  if (!value) return null;
  if (typeof value === 'string') return value;
  return value.id || value.player_id || null;
}

function rosterIds(list) {
  return (list || []).map(asPlayerId).filter(Boolean);
}

function normalizePartnerships(partnerships) {
  return (partnerships || [])
    .map((row) => {
      const player1Id = row.player1Id || row.player1_id;
      const player2Id = row.player2Id || row.player2_id;
      if (!player1Id || !player2Id) return null;
      return { player1Id, player2Id, player1_id: player1Id, player2_id: player2Id };
    })
    .filter(Boolean);
}

function scheduledRosterIds(game) {
  if (Array.isArray(game?.roster_player_ids) && game.roster_player_ids.length > 0) {
    return game.roster_player_ids.filter(Boolean);
  }
  return [
    game?.team1_player1_id,
    game?.team1_player2_id,
    game?.team2_player1_id,
    game?.team2_player2_id,
  ].filter(Boolean);
}

function isCompleteScheduledRoster(game, matchFormat) {
  if (game?.roster_complete === true) return true;
  if (game?.roster_complete === false) return false;
  const team1 = [game?.team1_player1_id, game?.team1_player2_id].filter(Boolean);
  const team2 = [game?.team2_player1_id, game?.team2_player2_id].filter(Boolean);
  if (matchFormat === 'singles') {
    return team1.length === 1 && team2.length === 1;
  }
  return team1.length === 2 && team2.length === 2;
}

function normalizeScheduledGames(scheduledGames, matchFormat) {
  return (scheduledGames || [])
    .filter((game) => game && game.status !== 'completed')
    .map((game) => {
      const pending = game.status == null || game.status === 'pending';
      return {
        ...game,
        roster_player_ids: scheduledRosterIds(game),
        roster_complete: pending && isCompleteScheduledRoster(game, matchFormat),
      };
    });
}

function normalizeInteractions(playerInteractions) {
  if (!playerInteractions) return {};
  if (playerInteractions instanceof Map) {
    return Object.fromEntries(playerInteractions.entries());
  }
  return { ...playerInteractions };
}

function normalizeMatchHistory(matchHistory) {
  return (matchHistory || []).map((game) => {
    const team1 = game.team1 || game.teams?.[0] || [];
    const team2 = game.team2 || game.teams?.[1] || [];
    return {
      gameId: game.gameId || game.id || null,
      team1: rosterIds(team1),
      team2: rosterIds(team2),
    };
  });
}

function waitMetricsForPlayer(playerId, playerWaitMetrics, player) {
  const metrics = playerWaitMetrics?.[playerId] || {};
  return {
    courtsObserved: metrics.courtsObserved ?? player?.courts_watched ?? 0,
    isOnCooldown: metrics.isOnCooldown ?? player?.isOnCooldown ?? false,
    lastCourtUpdate: metrics.lastCourtUpdate ?? player?.last_court_update ?? null,
    lastGameCompletedAt: metrics.lastGameCompletedAt ?? player?.last_game_completed_at ?? null,
    matchPreference: metrics.matchPreference ?? player?.match_preference ?? 'no_preference',
    preferenceStrength: metrics.preferenceStrength ?? player?.preference_strength ?? 'soft',
    mensDoublesEligible: metrics.mensDoublesEligible ?? player?.mens_doubles_eligible ?? false,
    mensDoublesShadowElo: metrics.mensDoublesShadowElo ?? player?.mens_doubles_shadow_elo ?? null,
  };
}

/**
 * Build MatchmakingInputV1 from the host's current live session state.
 * Only active players are included, matching `_materialise_session_matchmaking`.
 *
 * @param {object} liveState
 * @param {Array<{ court_type: 'active'|'next_up', index: number }>} targets
 */
export function buildMatchmakingInputV1FromLiveState(liveState, targets) {
  const session = liveState?.session || {};
  const playersById = liveState?.playersById || {};
  const sessionPlayerStatus = liveState?.sessionPlayerStatus || {};
  const waitingList = liveState?.waitingList || [];
  const courtAssignments = liveState?.courtAssignments || [];
  const nextAssignments = liveState?.nextAssignments || [];
  const playerWaitMetrics = liveState?.playerWaitMetrics || {};

  const sessionPlayers = [];
  const players = {};
  const seen = new Set();

  const pushActive = (playerId, fields) => {
    if (!playerId || seen.has(playerId)) return;
    const status = sessionPlayerStatus[playerId] || 'active';
    const onWaitlist = fields.player_list_status === 'waitlist';
    // Queue rows must be eligible even if a stale server fetch left them signed_up.
    if (status === 'paused' || status === 'inactive' || status === 'cancelled') return;
    if (status !== 'active' && !(onWaitlist && status === 'signed_up')) return;
    const player = playersById[playerId];
    if (!player) return;
    seen.add(playerId);
    sessionPlayers.push({
      player_id: playerId,
      player_list_status: fields.player_list_status,
      court_type: fields.court_type ?? null,
      court_number: fields.court_number ?? null,
      team_number: fields.team_number ?? null,
      waitlist_position: fields.waitlist_position ?? null,
      status: 'active',
      is_on_cooldown: Boolean(playerWaitMetrics[playerId]?.isOnCooldown || player.isOnCooldown),
    });
    players[playerId] = {
      id: playerId,
      name: playerName(player),
      gender: player.gender || null,
      mixed_elo_rating: player.mixed_elo_rating ?? 1200,
      levels_elo_rating: player.levels_elo_rating ?? 1200,
      singles_elo_rating: player.singles_elo_rating ?? null,
      gamesPlayedThisSession: player.gamesPlayedThisSession ?? player.effectiveGamesPlayed ?? 0,
      effectiveGamesPlayed: player.effectiveGamesPlayed ?? player.gamesPlayedThisSession ?? 0,
      isOnCooldown: Boolean(playerWaitMetrics[playerId]?.isOnCooldown || player.isOnCooldown),
      player_list_status: fields.player_list_status,
      court_number: fields.court_number ?? null,
      court_type: fields.court_type ?? null,
      team_number: fields.team_number ?? null,
      waitlist_position: fields.waitlist_position ?? null,
      mixed_category: player.mixed_category ?? null,
      levels_category: player.levels_category ?? null,
      category_calculated_at: player.category_calculated_at ?? null,
    };
  };

  waitingList.forEach((id, idx) => {
    pushActive(asPlayerId(id), { player_list_status: 'waitlist', waitlist_position: idx });
  });

  courtAssignments.forEach((court) => {
    const courtNumber = Number(court.courtNumber);
    rosterIds(court.team1).forEach((id) => {
      pushActive(id, {
        player_list_status: 'on_court',
        court_type: 'active',
        court_number: courtNumber,
        team_number: 1,
      });
    });
    rosterIds(court.team2).forEach((id) => {
      pushActive(id, {
        player_list_status: 'on_court',
        court_type: 'active',
        court_number: courtNumber,
        team_number: 2,
      });
    });
  });

  nextAssignments.forEach((slot, idx) => {
    const courtNumber = Number(slot.courtNumber ?? idx);
    rosterIds(slot.team1).forEach((id) => {
      pushActive(id, {
        player_list_status: 'next_up',
        court_type: 'next_up',
        court_number: courtNumber,
        team_number: 1,
      });
    });
    rosterIds(slot.team2).forEach((id) => {
      pushActive(id, {
        player_list_status: 'next_up',
        court_type: 'next_up',
        court_number: courtNumber,
        team_number: 2,
      });
    });
  });

  const sessionCourts = [
    ...courtAssignments.map((court) => ({
      court_type: 'active',
      court_number: Number(court.courtNumber),
    })),
    ...nextAssignments.map((slot, idx) => ({
      court_type: 'next_up',
      court_number: Number(slot.courtNumber ?? idx),
    })),
  ];

  const playerWaitMetricsOut = {};
  Object.keys(players).forEach((playerId) => {
    playerWaitMetricsOut[playerId] = waitMetricsForPlayer(playerId, playerWaitMetrics, playersById[playerId]);
  });

  const input = {
    schema_version: V1_SCHEMA_VERSION,
    session: {
      id: session.id ?? null,
      game_type_restriction: session.game_type_restriction ?? 'all_types',
      competitive_match_format: session.competitive_match_format ?? 'doubles',
      upcoming_queue_slot_count: session.upcoming_queue_slot_count ?? nextAssignments.length ?? 1,
      match_preferences_enabled: session.match_preferences_enabled !== false,
      cooldown_enabled: session.cooldown_enabled === true,
      relaxation_preset: session.relaxation_preset ?? 'default',
    },
    state: {
      session_players: sessionPlayers,
      session_courts: sessionCourts,
      players,
      partnerships: normalizePartnerships(liveState.partnerships),
      scheduled_games: normalizeScheduledGames(
        liveState.scheduledGames,
        session.competitive_match_format ?? 'doubles'
      ),
      match_history: normalizeMatchHistory(liveState.matchHistory),
      player_wait_metrics: playerWaitMetricsOut,
      player_interactions: normalizeInteractions(liveState.playerInteractions),
      excluded_player_ids: liveState.excludedPlayerIds || [],
      total_games_completed: liveState.totalGamesCompleted ?? (liveState.matchHistory || []).length,
      pending_finish_game: liveState.pendingFinishGame ?? null,
    },
    execution: {
      targets: (targets || []).map((target) => ({
        court_type: target.court_type,
        index: Number(target.index),
      })),
    },
  };

  return input;
}

/**
 * Build V1, validate strictly, and run the same engine the Edge Function uses.
 * @returns {{ ok: boolean, fills?: object[], errors?: string[], input?: object }}
 */
export function runLocalCourtFill(liveState, targets) {
  const flags = MATCHMAKING_CONFIG.featureFlags;
  const previousScoringFirst = flags.scoringFirstMatchmaking;
  // Offline fill must match hosted complete-session-score (MATCHMAKING_SCORING_FIRST).
  flags.scoringFirstMatchmaking = true;
  try {
    const input = buildMatchmakingInputV1FromLiveState(liveState, targets);
    const validation = validateMatchmakingInputV1(input, { strict: true });
    if (!validation.ok) {
      return { ok: false, errors: validation.errors, input };
    }
    const { fills } = runMatchmakingFromFrozenInput(input);
    return { ok: true, fills: fills || [], input };
  } finally {
    flags.scoringFirstMatchmaking = previousScoringFirst;
  }
}
